/**
 * Centralized navigation API for the inspector app.
 *
 * Central wrapper around React Router's navigate/location primitives.
 *
 * URLs are path-based (`/servers`, `/organizations/:orgId/billing`, etc.)
 * matching `react-router` semantics. Chatbox session hashes
 * (`#chatbox-slug`) are NOT app navigation and are preserved verbatim.
 */
import { useCallback, useContext, useLayoutEffect, useState } from "react";
import { UNSAFE_LocationContext, UNSAFE_NavigationContext } from "react-router";
import { getAppRouter } from "../router-ref";
import type { EvalRoute } from "./eval-route-types";
import type { EvalRoutePrefix } from "./eval-route-url";
import { normalizeHostedHashTab } from "./hosted-tab-policy";
import { listAppSurfaceNavSegments } from "@/shared/app-surfaces";

export type OrganizationRouteSection =
  | "overview"
  | "billing"
  | "models"
  | "slack";

/** Typed canonical paths used across the app. */
export const routePaths = {
  root: "/",
  home: "/home",
  servers: "/servers",
  hosts: "/hosts",
  hostCompare: "/host-compare",
  /** Chrome-less host-compare for vanity domains (caniuse.dev) — no sidebar/nav, bypasses NUX. */
  embedHostCompare: "/embed/host-compare",
  /** Chrome-less conformance-score runner for score.mcpjam.com. */
  embedScore: "/embed/score",
  /** Result of one score run, addressable only by its secret link token. */
  scoreResults: "/results",
  capabilities: "/capabilities",
  computer: "/computer",
  registry: "/registry",
  tools: "/tools",
  resources: "/resources",
  prompts: "/prompts",
  tasks: "/tasks",
  auth: "/auth",
  skills: "/skills",
  learning: "/learning",
  conformance: "/conformance",
  compatibility: "/compatibility",
  oauthFlow: "/oauth-flow",
  xaaFlow: "/xaa-flow",
  tracing: "/tracing",
  /** Legacy path. Still routed (it redirects), but never build links with it. */
  chatboxes: "/chatboxes",
  userTesting: "/user-testing",
  swarms: "/swarms",
  environments: "/environments",
  playground: "/playground",
  support: "/support",
  settings: "/settings",
  profile: "/profile",
  projectSettings: "/project-settings",
  callback: "/callback",
  billing: "/billing",
  evals: "/evals",
  /** Runs mode of Evaluate. Legacy `/ci-evals` URLs redirect here. */
  evalsRuns: "/evals/runs",
  organizations: "/organizations",
} as const;

export type RoutePath = (typeof routePaths)[keyof typeof routePaths] | string;

/** Build a path that deep-links to a specific host's canvas, or to the hosts hub. */
export function buildHostsPath(hostId?: string | null): string {
  if (!hostId) return routePaths.hosts;
  return `${routePaths.hosts}/${encodeURIComponent(hostId)}`;
}

/** Build a path that deep-links into Compare with a pre-selected set of hosts. */
export function buildHostComparePath(
  hostIds?: ReadonlyArray<string> | null
): string {
  if (!hostIds || hostIds.length === 0) return routePaths.hostCompare;
  const param = hostIds.map((id) => id.trim()).filter((id) => id.length > 0);
  if (param.length === 0) return routePaths.hostCompare;
  const search = new URLSearchParams({ hosts: param.join(",") });
  return `${routePaths.hostCompare}?${search.toString()}`;
}


/** The create route. A static segment, so it outranks `:scenarioId`. */
export const userTestingCreatePath = `${routePaths.userTesting}/new`;

/** Sub-tabs on `/user-testing/:scenarioId`. Sessions is the landing tab. */
export type UserTestingDetailTab = "sessions" | "clusters";

/**
 * Build a path to one User Testing scenario. `scenarioId` is the scenario's
 * CHATBOX id — the identity host-backed and environment-backed scenarios
 * share. A HOST id is still accepted by the surface (links minted under the
 * older scheme redirect onto the chatbox id), but new links should never be
 * built with one. `session` opens straight into one tester session, which is
 * what a copied session link carries.
 */
export function buildUserTestingScenarioPath(
  scenarioId: string,
  opts: { tab?: UserTestingDetailTab; session?: string } = {}
): string {
  const base = `${routePaths.userTesting}/${encodeURIComponent(scenarioId)}`;
  const search = new URLSearchParams();
  if (opts.tab && opts.tab !== "sessions") search.set("tab", opts.tab);
  if (opts.session) search.set("session", opts.session);
  const query = search.toString();
  return query ? `${base}?${query}` : base;
}

/** Parse the sub-tab query on a scenario path. Unknown / missing → sessions. */
export function parseUserTestingDetailTab(
  search: string
): UserTestingDetailTab {
  return new URLSearchParams(search).get("tab") === "clusters"
    ? "clusters"
    : "sessions";
}

/** The Swarms create route. Static, so it outranks `:swarmId`. */
export const swarmsCreatePath = `${routePaths.swarms}/new`;

/** Detail tabs on `/swarms/:swarmId`. Insights is the default landing tab. */
export type SwarmDetailTab = "insights" | "sessions";

/**
 * Build a path to one Swarm Run (wave) detail. `swarmId` is the durable
 * `swarmRunGroupId` when present, otherwise the wave's newest journey-run id.
 */
export function buildSwarmPath(
  swarmId: string,
  opts: {
    tab?: SwarmDetailTab;
    session?: string;
    sel?: string;
  } = {},
): string {
  const base = `${routePaths.swarms}/${encodeURIComponent(swarmId)}`;
  const search = new URLSearchParams();
  if (opts.tab && opts.tab !== "insights") search.set("tab", opts.tab);
  if (opts.session) search.set("session", opts.session);
  if (opts.sel) search.set("sel", opts.sel);
  const query = search.toString();
  return query ? `${base}?${query}` : base;
}

/**
 * Parse the detail-tab query on a Swarm Run path. Unknown / missing / legacy
 * `overview` or `personas` → insights (personas now live on Insights).
 */
export function parseSwarmDetailTab(search: string): SwarmDetailTab {
  const value = new URLSearchParams(search).get("tab");
  if (value === "sessions") return value;
  return "insights";
}

/**
 * Build a Swarms deep-link to one synthetic session. Unlike the chatbox
 * Sessions tab (host-anchored), the Swarms surface is Persona → Journey → Run →
 * Session, so a link that only carried `host`/`session` couldn't restore the
 * persona + run selection the recipient needs to reach the session. This
 * encodes `persona` (personaRefId) and `run` (runId) alongside `host`/`session`
 * so `SwarmsTab` can restore the full selection chain on load.
 */
export function buildSwarmSessionPath(args: {
  personaRefId: string;
  runId: string;
  hostId: string;
  threadId: string;
}): string {
  const search = new URLSearchParams({
    persona: args.personaRefId,
    run: args.runId,
    host: args.hostId,
    session: args.threadId,
  });
  return `${routePaths.swarms}?${search.toString()}`;
}

/**
 * Parse a Swarms session deep-link's selection params (see
 * {@link buildSwarmSessionPath}) from a search string. Every field is optional —
 * a bare `/swarms` visit returns all-undefined.
 */
export function parseSwarmSessionParams(search: string): {
  personaRefId?: string;
  runId?: string;
  hostId?: string;
  threadId?: string;
} {
  const params = new URLSearchParams(search);
  const pick = (key: string) => {
    const value = params.get(key);
    return value && value.trim() ? value : undefined;
  };
  return {
    personaRefId: pick("persona"),
    runId: pick("run"),
    hostId: pick("host"),
    threadId: pick("session"),
  };
}

/** Build a path for a specific organization route. */
export function buildOrganizationPath(
  orgId: string,
  section?: OrganizationRouteSection
): string {
  if (section === "billing") return `/organizations/${orgId}/billing`;
  if (section === "models") return `/organizations/${orgId}/models`;
  // The Slack section's sub-tabs live in `?tab=`, not in the path: they are
  // one settings screen with three views, not three org routes, and keeping
  // them out of the path means the nav, the surface manifest and the route
  // table each gain exactly one entry.
  if (section === "slack") return `/organizations/${orgId}/slack`;
  return `/organizations/${orgId}`;
}

/**
 * Build an eval route path in Suites mode from a typed EvalRoute.
 */
export function buildEvalsPath(route: EvalRoute): string {
  return buildEvalRoutePath(routePaths.evals, route);
}

/** Build the same typed EvalRoute in Runs mode (`/evals/runs/...`). */
export function buildEvalsRunsPath(route: EvalRoute): string {
  return buildEvalRoutePath(routePaths.evalsRuns, route);
}

/**
 * Legacy `/ci-evals/*` → `/evals/runs/*`, for the router's redirect loader.
 *
 * A raw-string prefix rewrite rather than a rebuild from route params: the
 * sub-tree is matched with a splat, and the string form preserves commit SHAs
 * and suite ids exactly as they were encoded. Query and hash come along —
 * commit links carry `?suite=&iteration=`, run links carry
 * `?iteration=&case=&compareTo=`, and anything can carry `?project=`.
 *
 * These URLs shipped in CI logs, bookmarks, and the SDK quickstart's
 * post-sign-in return path, so they redirect rather than 404 into the
 * catch-all (which renders Servers — a silently wrong landing page).
 */
export function legacyCiEvalsPathToRunsPath(
  pathname: string,
  search = "",
  hash = ""
): string {
  return `${pathname.replace(
    /^\/ci-evals/,
    routePaths.evalsRuns
  )}${search}${hash}`;
}

function buildEvalRoutePath(prefix: EvalRoutePrefix, route: EvalRoute): string {
  switch (route.type) {
    case "list":
      return prefix;
    case "create":
      return `${prefix}/create`;
    case "suite-overview": {
      const params = new URLSearchParams();
      if (route.view && route.view !== "runs") params.set("view", route.view);
      if (route.fromCommit) params.set("fromCommit", route.fromCommit);
      const query = params.toString();
      return `${prefix}/suite/${encodeURIComponent(route.suiteId)}${
        query ? `?${query}` : ""
      }`;
    }
    case "run-detail": {
      const params = new URLSearchParams();
      if (route.iteration) params.set("iteration", route.iteration);
      if (route.testCaseId) params.set("case", route.testCaseId);
      if (route.insightsFocus) params.set("insights", "1");
      if (route.compareToRunId) params.set("compareTo", route.compareToRunId);
      const query = params.toString();
      return `${prefix}/suite/${encodeURIComponent(
        route.suiteId
      )}/runs/${encodeURIComponent(route.runId)}${query ? `?${query}` : ""}`;
    }
    case "test-detail": {
      const params = new URLSearchParams();
      if (route.iteration) params.set("iteration", route.iteration);
      const query = params.toString();
      return `${prefix}/suite/${encodeURIComponent(
        route.suiteId
      )}/test/${encodeURIComponent(route.testId)}${query ? `?${query}` : ""}`;
    }
    case "test-edit": {
      const params = new URLSearchParams();
      if (route.openCompare) params.set("compare", "1");
      if (route.iteration) params.set("iteration", route.iteration);
      const query = params.toString();
      return `${prefix}/suite/${encodeURIComponent(
        route.suiteId
      )}/test/${encodeURIComponent(route.testId)}/edit${
        query ? `?${query}` : ""
      }`;
    }
    case "suite-edit":
      return `${prefix}/suite/${encodeURIComponent(route.suiteId)}/edit`;
    case "commit-detail": {
      // Commits are a Runs-mode lens: Suites mode has no cross-suite SHA view,
      // so a commit route built there degrades to that mode's list.
      if (prefix !== routePaths.evalsRuns) return prefix;
      const params = new URLSearchParams();
      if (route.suite) params.set("suite", route.suite);
      if (route.iteration) params.set("iteration", route.iteration);
      const query = params.toString();
      return `${prefix}/commit/${encodeURIComponent(route.commitSha)}${
        query ? `?${query}` : ""
      }`;
    }
  }
}

export interface AppNavigateOptions {
  replace?: boolean;
}

/**
 * Imperative navigate from a non-React caller (IPC bridge, OAuth callback).
 *
 * Prefer `useAppNavigate()` inside components. Falls back to writing
 * `window.history` directly if the router has not yet been created
 * (e.g. very early bootstrap).
 */
export function navigateApp(to: string, options?: AppNavigateOptions): void {
  const router = getAppRouter();
  if (router) {
    void router.navigate(to, { replace: options?.replace });
    return;
  }
  if (options?.replace) {
    window.history.replaceState({}, "", to);
  } else {
    window.history.pushState({}, "", to);
  }
  window.dispatchEvent(new PopStateEvent("popstate"));
}

/**
 * React hook returning a typed navigate function.
 *
 * Reads the router's navigation context directly so the hook call shape is
 * unconditional (Rules of Hooks compliant). When mounted outside a Router
 * (e.g. component tests rendering without a `<MemoryRouter>`), the navigator
 * context is undefined and the callback falls back to `navigateApp`.
 */
export function useAppNavigate() {
  const navigationContext = useContext(UNSAFE_NavigationContext);
  const navigator = navigationContext?.navigator;
  return useCallback(
    (to: string, options?: AppNavigateOptions) => {
      if (navigator) {
        if (options?.replace) {
          navigator.replace(to);
        } else {
          navigator.push(to);
        }
        return;
      }
      navigateApp(to, options);
    },
    [navigator]
  );
}

/**
 * Strip the leading slash from the first pathname segment so `useActiveTab()`
 * returns `"servers"` (matching the legacy `activeTab` state shape).
 *
 * Phase 3: this is the single source of truth for `activeTab`; App.tsx keeps
 * the old render tree but reads the tab from the URL.
 */
export function useActiveTab(): string {
  const locationContext = useContext(UNSAFE_LocationContext);
  const [fallbackPathname, setFallbackPathname] = useState(
    getWindowFallbackPathname
  );

  useLayoutEffect(() => {
    if (locationContext || typeof window === "undefined") return;
    const syncFallbackPathname = () => {
      setFallbackPathname(getWindowFallbackPathname());
    };
    window.addEventListener("popstate", syncFallbackPathname);
    return () => {
      window.removeEventListener("popstate", syncFallbackPathname);
    };
  }, [locationContext]);

  const pathname = locationContext?.location.pathname ?? fallbackPathname;
  return pathnameToActiveTab(pathname);
}

/**
 * Every tab segment that resolves to a real screen — DERIVED from the
 * surface manifests (`shared/app-surfaces.ts`), which are also what the
 * agent's atlas and the route-coverage tests read.
 *
 * It used to be a hand-maintained union of the hosted-policy lists plus a
 * few literals, which is a drift hazard in the one direction that matters:
 * add a screen, forget this list, and the screen silently becomes
 * unreachable by `ui_navigate` while `pathnameToActiveTab` quietly resolves
 * it to Servers. Now the manifest is the single place to add.
 *
 * The hosted policy lists (`hosted-tab-policy.ts`) stay as a FILTER over
 * these segments — availability is a separate question from existence — and
 * a test asserts every policy entry still names a real segment.
 */
const KNOWN_APP_TAB_SEGMENTS = new Set<string>(listAppSurfaceNavSegments());

export function isKnownAppTabSegment(segment: string): boolean {
  return KNOWN_APP_TAB_SEGMENTS.has(segment);
}

export function listKnownAppTabSegments(): string[] {
  return [...KNOWN_APP_TAB_SEGMENTS];
}

function isSpecialEntryPathname(pathname: string): boolean {
  return (
    pathname === "/billing" ||
    pathname === "/billing/" ||
    pathname === "/callback" ||
    pathname === "/callback/" ||
    pathname.startsWith("/oauth/callback")
  );
}

/**
 * The OAuth debugger callback (`/oauth/callback/debug`) runs in a throwaway
 * popup that only relays its code to the opener and closes. It must render
 * WITHOUT `<AuthKitProvider>` (see main.tsx): AuthKit's on-load refresh rotates
 * the shared WorkOS token from this short-lived context, intermittently
 * dropping the opener window's session.
 */
export function isDebugOAuthCallbackPath(pathname: string): boolean {
  return (
    pathname === "/oauth/callback/debug" ||
    pathname.startsWith("/oauth/callback/debug/")
  );
}

export function pathnameToActiveTab(pathname: string): string {
  if (isSpecialEntryPathname(pathname)) return "servers";
  if (pathname.startsWith(`${routePaths.capabilities}/`)) {
    return "host-compare";
  }
  const firstSegment = pathname.replace(/^\/+/, "").split("/")[0] || "home";
  const normalized = normalizeHostedHashTab(firstSegment);
  // Unknown first segments include chatbox slugs; App handles those surfaces
  // before route rendering, so the shell falls back to the safe servers body.
  return KNOWN_APP_TAB_SEGMENTS.has(normalized) ? normalized : "servers";
}

function getWindowFallbackPathname(): string {
  if (typeof window === "undefined") return "/";
  return window.location.pathname || "/";
}

export interface CurrentOrgRoute {
  orgId: string;
  orgSection: OrganizationRouteSection;
}

export function useCurrentOrgRoute(): CurrentOrgRoute | null {
  const locationContext = useContext(UNSAFE_LocationContext);
  const pathname =
    locationContext?.location.pathname ??
    (typeof window === "undefined" ? "/" : window.location.pathname);
  const segments = pathname.replace(/^\/+/, "").split("/");
  if (segments[0] !== "organizations") return null;
  const orgId = segments[1];
  if (!orgId) return null;
  const sectionSegment = segments[2];
  const orgSection: OrganizationRouteSection =
    sectionSegment === "billing"
      ? "billing"
      : sectionSegment === "models"
      ? "models"
      : sectionSegment === "slack"
      ? "slack"
      : "overview";
  return { orgId: decodePathSegment(orgId), orgSection };
}

/**
 * One query-string parameter from the current location.
 *
 * Reads the router's location context directly — with a `window` fallback —
 * for the same reason `useActiveTab` does: components in this app are rendered
 * without a `<Router>` in unit tests, and `useSearchParams` throws there.
 * Subscribing to the context (rather than reading `window.location` alone) is
 * what makes a `?tab=` change re-render the component that reads it.
 */
export function useCurrentSearchParam(name: string): string | null {
  const locationContext = useContext(UNSAFE_LocationContext);
  const [fallbackSearch, setFallbackSearch] = useState(getWindowFallbackSearch);

  // Mirrors `useActiveTab`: without the listener the no-router path reads the
  // query string once and never again, so a `?tab=` change would move history
  // and leave the component rendering the previous tab.
  useLayoutEffect(() => {
    if (locationContext || typeof window === "undefined") return;
    const syncFallbackSearch = () => setFallbackSearch(getWindowFallbackSearch());
    window.addEventListener("popstate", syncFallbackSearch);
    return () => {
      window.removeEventListener("popstate", syncFallbackSearch);
    };
  }, [locationContext]);

  const search = locationContext?.location.search ?? fallbackSearch;
  return new URLSearchParams(search).get(name);
}

function getWindowFallbackSearch(): string {
  if (typeof window === "undefined") return "";
  return window.location.search || "";
}

function decodePathSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

export function navigationTargetToPath(
  rawTarget: string,
  fallback: string = routePaths.servers
): string {
  const stripped = rawTarget.replace(/^#/, "").replace(/^\/+/, "");
  const questionIndex = stripped.indexOf("?");
  const pathPart =
    questionIndex === -1 ? stripped : stripped.slice(0, questionIndex);
  const queryPart = questionIndex === -1 ? "" : stripped.slice(questionIndex);
  const segments = pathPart.split("/").filter(Boolean);
  const normalizedTab = normalizeHostedHashTab(segments[0] || "servers");
  if (!KNOWN_APP_TAB_SEGMENTS.has(normalizedTab)) return fallback;
  // The tab id and the public path segment agree everywhere except User
  // Testing, whose tab id stayed `chatboxes`. Emit the canonical path so
  // agent navigation and legacy bookmarks land directly instead of bouncing
  // through the `/chatboxes` redirect.
  const pathSegment =
    normalizedTab === "chatboxes" ? "user-testing" : normalizedTab;
  return `/${[pathSegment, ...segments.slice(1)].join("/")}${queryPart}`;
}

export function legacyHashBookmarkToPath(hash: string): string | null {
  const fragment = hash.replace(/^#\/?/, "");
  if (!fragment) return null;
  const firstSegment = fragment.split(/[/?]/)[0] || "";
  const normalizedFirstSegment = normalizeHostedHashTab(firstSegment);
  if (!KNOWN_APP_TAB_SEGMENTS.has(normalizedFirstSegment)) return null;
  const normalizedFragment =
    normalizedFirstSegment === firstSegment
      ? fragment
      : `${normalizedFirstSegment}${fragment.slice(firstSegment.length)}`;
  return navigationTargetToPath(normalizedFragment);
}

export function normalizeInitialLegacyHashBookmark(): void {
  if (typeof window === "undefined") return;
  const pathname = window.location.pathname || "/";
  if (pathname !== "/" && pathname !== "") return;
  const path = legacyHashBookmarkToPath(window.location.hash);
  if (!path) return;
  window.history.replaceState({}, "", path);
}

export function normalizeReturnTargetPath(
  target?: string | null,
  fallback: string = routePaths.servers
): string {
  const trimmed = target?.trim() ?? "";
  if (!trimmed) return fallback;
  return navigationTargetToPath(trimmed, fallback);
}

export function captureCurrentReturnPath(): string | null {
  if (typeof window === "undefined") return null;
  const pathname = window.location.pathname || routePaths.root;
  const search = window.location.search || "";
  if (pathname === routePaths.root || pathname === "") return null;
  return `${pathname}${search}`;
}

export function getProjectSwitchNavigationTarget({
  activeTab,
  activeOrganizationId,
  nextProjectOrganizationId,
}: {
  activeTab: string;
  activeOrganizationId?: string;
  nextProjectOrganizationId?: string;
}): string | null {
  if (activeTab !== "organizations") {
    return null;
  }

  if (
    !activeOrganizationId ||
    !nextProjectOrganizationId ||
    nextProjectOrganizationId !== activeOrganizationId
  ) {
    return routePaths.servers;
  }

  return null;
}

export function getInvalidOrganizationRouteNavigationTarget({
  routeTab,
  routeOrganizationId,
  isLoadingOrganizations,
  hasRouteOrganization,
}: {
  routeTab: string;
  routeOrganizationId?: string;
  isLoadingOrganizations: boolean;
  hasRouteOrganization: boolean;
}): string | null {
  if (routeTab !== "organizations" || isLoadingOrganizations) {
    return null;
  }

  if (!routeOrganizationId || !hasRouteOrganization) {
    return routePaths.servers;
  }

  return null;
}

/**
 * Decide whether an active-project change should snap the user to Servers.
 *
 * Staying on a project-scoped page (App Builder/Chat) after the active project
 * changes would leave the user pointed at a project that no longer exists, so
 * we snap to Servers. But not every project change is a manual switch: opening
 * another org's settings (e.g. via the switcher's per-row gear) flips the
 * active org, which auto-resolves a new active project as a *side effect* while
 * the user deliberately navigates to the org page. Organization routes are
 * org-scoped, not project-scoped, so snapping there would bounce the user right
 * back off the settings they just opened.
 *
 * The initial hydration (no previous id) and the local-default `"none"`
 * placeholder are never treated as real switches.
 */
export function shouldSnapToServersOnActiveProjectChange({
  previousActiveProjectId,
  nextActiveProjectId,
  activeTab,
}: {
  previousActiveProjectId: string | null;
  nextActiveProjectId: string;
  activeTab: string;
}): boolean {
  if (
    previousActiveProjectId == null ||
    previousActiveProjectId === nextActiveProjectId ||
    previousActiveProjectId === "none" ||
    nextActiveProjectId === "none"
  ) {
    return false;
  }

  if (activeTab === "organizations") {
    return false;
  }

  return true;
}
